import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { ChevronLeft, ChevronRight, ChevronDown, AlertCircle, TrendingDown, FileText } from 'lucide-react';

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function monthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

function prevMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function fmtDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-PK', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function mergeByDate(added, received) {
  const map = {};
  for (const r of added) {
    if (!map[r.date]) map[r.date] = { date: r.date, added: 0, received: 0, notes: [] };
    map[r.date].added += r.amount;
  }
  for (const r of received) {
    if (!map[r.date]) map[r.date] = { date: r.date, added: 0, received: 0, notes: [] };
    map[r.date].received += r.amount;
    if (r.notes && r.notes.trim()) map[r.date].notes.push(r.notes.trim());
  }
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

export default function OfficerPendings() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedRow(null);
    try {
      const res = await api.get('/recovery-officer-pendings/my-pendings/', { params: { month } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load pending data.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const today = currentMonth();

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">

      {/* All-time pending box */}
      {data && (
        <div className={`mb-5 rounded-xl p-4 border flex items-center gap-4 ${data.total_pending_all_time > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${data.total_pending_all_time > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
            <TrendingDown size={20} className={data.total_pending_all_time > 0 ? 'text-red-600' : 'text-green-600'} />
          </div>
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${data.total_pending_all_time > 0 ? 'text-red-500' : 'text-green-500'}`}>
              Total Pending (All Months)
            </p>
            <p className={`text-2xl font-bold ${data.total_pending_all_time > 0 ? 'text-red-700' : 'text-green-700'}`}>
              PKR {fmt(data.total_pending_all_time)}
            </p>
            <p className={`text-xs mt-0.5 ${data.total_pending_all_time > 0 ? 'text-red-400' : 'text-green-400'}`}>
              Cumulative outstanding across all months
            </p>
          </div>
        </div>
      )}

      {/* Month navigator */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => setMonth(prevMonth(month))}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          <ChevronLeft size={18} />
        </button>
        <h2 className="text-lg font-semibold text-gray-800">{monthLabel(month)}</h2>
        <button
          onClick={() => setMonth(nextMonth(month))}
          disabled={month >= today}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {loading && (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg p-4">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && (() => {
        const rows = mergeByDate(data.daily_breakdown, data.daily_receipts || []);
        return (
          <>
            {/* Monthly summary cards */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
                <p className="text-xs text-blue-500 font-medium mb-1">Pending Added</p>
                <p className="text-xl font-bold text-blue-700">PKR {fmt(data.total_assigned)}</p>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
                <p className="text-xs text-green-500 font-medium mb-1">Pending Received</p>
                <p className="text-xl font-bold text-green-700">PKR {fmt(data.total_received)}</p>
              </div>
              <div className={`rounded-xl p-4 text-center border ${data.outstanding > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                <p className={`text-xs font-medium mb-1 ${data.outstanding > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                  This Month Net
                </p>
                <p className={`text-xl font-bold ${data.outstanding > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                  PKR {fmt(data.outstanding)}
                </p>
              </div>
            </div>

            {/* Combined daily table */}
            {rows.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No entries for {monthLabel(month)}.
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Daily Breakdown — {monthLabel(month)}
                  </h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Date</th>
                      <th className="text-right px-4 py-2.5 text-blue-500 font-medium">Pending Added</th>
                      <th className="text-right px-4 py-2.5 text-green-500 font-medium">Pending Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const hasNotes = row.notes.length > 0;
                      const isOpen = expandedRow === row.date;
                      return (
                        <React.Fragment key={i}>
                          <tr className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">{fmtDate(row.date)}</td>
                            <td className="px-4 py-3 text-right font-medium text-blue-700">
                              {row.added > 0 ? `PKR ${fmt(row.added)}` : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-green-700">
                              {row.received > 0 ? (
                                <button
                                  onClick={() => hasNotes && setExpandedRow(isOpen ? null : row.date)}
                                  className={`inline-flex items-center gap-1.5 ${hasNotes ? 'cursor-pointer hover:text-green-900' : 'cursor-default'}`}
                                >
                                  PKR {fmt(row.received)}
                                  {hasNotes && (
                                    <span className="inline-flex items-center gap-0.5 text-xs text-green-500">
                                      <FileText size={12} />
                                      <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                    </span>
                                  )}
                                </button>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                          {isOpen && hasNotes && (
                            <tr className="bg-green-50 border-b border-green-100">
                              <td colSpan={3} className="px-4 py-2.5">
                                {row.notes.map((note, ni) => (
                                  <p key={ni} className="text-sm text-green-800 flex items-start gap-2">
                                    <FileText size={13} className="mt-0.5 flex-shrink-0 text-green-400" />
                                    {note}
                                  </p>
                                ))}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-200">
                      <td className="px-4 py-3 font-bold text-gray-700">Totals</td>
                      <td className="px-4 py-3 text-right font-bold text-blue-700">
                        PKR {fmt(data.total_assigned)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">
                        PKR {fmt(data.total_received)}
                      </td>
                    </tr>
                    <tr className="bg-gray-100 border-t border-gray-200">
                      <td className="px-4 py-3 font-bold text-gray-700" colSpan={2}>
                        Current Pending (this month)
                      </td>
                      <td className={`px-4 py-3 text-right font-bold text-lg ${data.outstanding > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        PKR {fmt(data.outstanding)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
